import React, { useState, useEffect, useRef, cloneElement } from "react";
import PropTypes from "prop-types";

import { BASE_ASSETS_URL } from "constants/app";
import scrollToRef from "utils/scrollToRef";
import styles from "./accordion.scss";

const VARIANTS = {
  ROUND: "round",
  COMPACT: "compact",
  ARROW_WITH_TEXT: "arrow_with_text",
};

const BUTTON_VARIANT_MAP = {
  [VARIANTS.COMPACT]: {
    expanded: <i className={`${styles.arrow} ${styles.arrow_up}`}></i>,
    collapsed: <i className={`${styles.arrow} ${styles.arrow_down}`}></i>,
  },
  [VARIANTS.ARROW_WITH_TEXT]: {
    expanded: (
      <div className={styles.arrow_with_text}>
        <span className={styles.view_text}>Hide Details</span> <i className={`${styles.arrow} ${styles.arrow_up}`}></i>
      </div>
    ),
    collapsed: (
      <div className={styles.arrow_with_text}>
        <span className={styles.view_text}>View Details</span> <i className={`${styles.arrow} ${styles.arrow_down}`}></i>
      </div>
    ),
  },
  [VARIANTS.ROUND]: {
    expanded: (
      <div className={`${styles.round} ${styles.round_button}`}>
        <i className={`${styles.arrow} ${styles.arrow_up}`}></i>
      </div>
    ),
    collapsed: (
      <div className={`${styles.round} ${styles.round_button}`}>
        <i className={`${styles.arrow}  ${styles.arrow_down}`}></i>
      </div>
    ),
  },
};

const Accordion = React.forwardRef((props, ref) => {
  // component data
  const { title, autoScrollOnExpand, scrollOnExpand, children, variant, hideActionButton } = props;
  // flags
  const { isValidated, isEditable, isExpanded, disabled } = props;
  // event handlers
  const { onEditClick, onExpandClick } = props;

  const [isAccordionExpanded, setIsAccordionExpanded] = useState(isExpanded);
  const accordionRef = useRef();

  useEffect(() => {
    if (isEditable) {
      setIsAccordionExpanded(true);
    }
  }, [isEditable]);

  useEffect(() => {
    setIsAccordionExpanded(isExpanded);
    if (autoScrollOnExpand && isExpanded) {
      setTimeout(() => scrollOnExpand(accordionRef), 400);
    }
  }, [isExpanded]);

  const handleExpandClick = () => {
    if (!disabled) {
      if (onExpandClick) {
        onExpandClick(!isAccordionExpanded);
      }
      if (autoScrollOnExpand && !isAccordionExpanded) {
        setTimeout(() => scrollOnExpand(accordionRef), 400);
      }
      setIsAccordionExpanded((isAccordionExpanded) => !isAccordionExpanded);
    }
  };

  const renderAccordionButtons = () => {
    const { expanded, collapsed } = BUTTON_VARIANT_MAP[variant];
    const button = cloneElement(isAccordionExpanded ? expanded : collapsed, { onClick: handleExpandClick });
    return button;
  };

  return (
    <div ref={accordionRef} className={styles.accordionContainer}>
      <div ref={ref} className={styles.accordion}>
        <div className={styles.accordionHeader}>
          <div className={styles.accordionTitle}>
            {isValidated !== undefined && (
              <span className={`${styles.icon} ${isValidated ? styles.validated : ""}`}>
                <img src={`${BASE_ASSETS_URL}images/tick.svg`} alt="Selected" />
              </span>
            )}
            <h4 className={styles.heading}>{title}</h4>
          </div>
          <div className={styles.buttons}>
            {isEditable !== undefined && (
              <i
                className={`material-icons-outlined ${styles.edit} ${isEditable ? styles.active : ""}`}
                onClick={() => onEditClick()}
              >
                edit
              </i>
            )}
            {isExpanded !== null && !hideActionButton && renderAccordionButtons()}
          </div>
        </div>
        {isAccordionExpanded && children && <div className="b2b-lib-accordionContent">{children}</div>}
      </div>
    </div>
  );
});

Accordion.defaultProps = {
  autoScrollOnExpand: true,
  isExpanded: false,
  scrollOnExpand: (ref) => scrollToRef(ref, { behavior: "smooth", block: "center" }),
  variant: VARIANTS.ARROW_WITH_TEXT,
  disabled: false,
  hideActionButton: false,
};

Accordion.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  children: PropTypes.element,
  autoScrollOnExpand: PropTypes.bool,
  scrollOnExpand: PropTypes.func,
  isValidated: PropTypes.bool,
  isEditable: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onEditClick: PropTypes.func,
  onExpandClick: PropTypes.func,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  hideActionButton: PropTypes.bool,
};

export default Accordion;
